interface FortWorthData {
  address?: string;
  zone?: string;
  overlayDistrict?: string;
  councilDistrict?: string;
  cityLimit?: string;
  schoolDistrict?: string;
  parcelId?: string;
  ownerName?: string;
  ownerAddress?: string;
  ownerCityState?: string;
  ownerZip?: string;
  legalDescription?: string;
  situsAddress?: string;
  acres?: number;
  countyName?: string;
  cityName?: string;
  zipCode?: string;
  subdivisionName?: string;
  appraisedValue?: number;
  marketValue?: number;
  mapLink?: string;
}

const hasValue = (value: unknown): value is string | number => {
  if (value == null) return false;
  if (typeof value === 'number') return !Number.isNaN(value);
  return value.trim().length > 0;
};

const formatCurrency = (value?: number): string | null => {
  if (!hasValue(value)) return null;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
};

const formatOwner = (data: FortWorthData): string | null => {
  const parts = [data.ownerName, data.ownerAddress, data.ownerCityState, data.ownerZip].filter(hasValue);
  return parts.length > 0 ? parts.join(', ') : null;
};

export const formatFortWorthData = (data: FortWorthData): string => {
  const rows = [
    { label: 'Address', value: data.address || 'No address available' },
    { label: 'Zone', value: data.zone },
    { label: 'Overlay District', value: data.overlayDistrict },
    { label: 'Council District', value: data.councilDistrict },
    { label: 'City Limit', value: data.cityLimit },
    { label: 'School District', value: data.schoolDistrict },
    { label: 'Parcel ID', value: data.parcelId },
    { label: 'Owner', value: formatOwner(data) },
    { label: 'Legal Description', value: data.legalDescription },
    { label: 'Property Size', value: hasValue(data.acres) ? `${data.acres} acres` : null },
    { label: 'Subdivision', value: data.subdivisionName },
    { label: 'City', value: data.cityName },
    { label: 'County', value: data.countyName },
    { label: 'ZIP Code', value: data.zipCode },
    { label: 'Appraised Value', value: formatCurrency(data.appraisedValue) },
    { label: 'Market Value', value: formatCurrency(data.marketValue) },
    { label: 'GIS Map', value: data.mapLink ? `[View in GIS](${data.mapLink})` : null },
  ];

  return rows
    .filter(({ value }) => hasValue(value))
    .map(({ label, value }) => `| **${label}** | ${value} |`)
    .join('\n');
};
